/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.19.0
 * @since 1.19.0
 */

import { createElement } from 'react';

import { BaseQueryApi } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { FetchArgs } from '@reduxjs/toolkit/dist/query/fetchBaseQuery';
import { fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';

import packageInfo from '../../../package.json';
import { API_HOSTNAME, API_VERSION_DEFAULT } from '../modules/app/Api';
import { appRequestV2 } from '../modules/app/appRequest';
import { EAppFeatures } from '../modules/app/appTypes';
import ModalVersionUpdate from '../modules/modal/components/ModalVersionUpdate';
import { modalOpen } from '../modules/modal/modalActions';
import { IModalRenderProps } from '../modules/modal/types/modalTypes';
import { ECookieKey } from '../modules/storage/storageTypes';
import { getCookieByName } from '../modules/storage/storageUtils';
import { cacheUserDataStart } from '../pages/PageAuth/actions/authCacheActions';
import { IRootReducer } from '../reducers/rootReducer';
import { openExternalRoute } from '../routes/constants/externalRoutes';
import { RoutePath } from '../routes/constants/routesConstants';

export const baseQueryFn = fetchBaseQuery({
	baseUrl: `${API_HOSTNAME}/${API_VERSION_DEFAULT}`,
	prepareHeaders: (headers: Headers) => {
		headers.set('Pragma', 'no-cache');
		headers.set('UI-Version', packageInfo.version);
		headers.set('x-csrf-token', getCookieByName(ECookieKey.XSRF_TOKEN));
		return headers;
	},
	fetchFn: (input: RequestInfo, init?: RequestInit) => appRequestV2(input, init),
});

export const baseQuery = async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: object) => {
	const result = await baseQueryFn(args, api, extraOptions);
	const response = result.meta?.response;

	if (response) {
		const unauthorized = response.status === 401;
		if (unauthorized) {
			// После протухания токена необходимо принудительно обновлять страницу пользователя
			openExternalRoute(RoutePath.unauthorized);
		}

		const actualVersion = response.headers.get('UI-Version');
		// Проверка текущей версии приложения
		if (actualVersion && actualVersion !== packageInfo.version) {
			const state = api.getState() as IRootReducer;

			const checkUIVersion = state?.app.config.features?.find(
				(f) => f.name === EAppFeatures.showUIVersionModal,
			)?.enabled;
			if (checkUIVersion) {
				api.dispatch(
					modalOpen({
						onCloseCallback: () => {
							location.reload();
						},
						render: (p: IModalRenderProps) =>
							createElement(ModalVersionUpdate, {
								onCloseModal: p.onCloseModal,
							}),
					}),
				);
			}
		}

		const appLocation = response.headers.get('location');
		const cached = !appLocation ? true : appLocation.indexOf('waitLogin') !== 0;
		if (!cached) {
			api.dispatch(cacheUserDataStart());
		}
	}
	if (result.data) {
		return result.data;
	} else if (result.error) {
		return {
			// @ts-ignore
			error: result.error.data?.error ?? {
				code: 'unknown.error',
				message: 'Unable to get error from response',
			},
		} as unknown as any;
	}
	return {};
};
